import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDemo, onViewCode }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getTechColor = (tech) => {
    const colors = {
      'React': 'bg-blue-100 text-blue-800',
      'Node.js': 'bg-green-100 text-green-800',
      'Python': 'bg-yellow-100 text-yellow-800',
      'TypeScript': 'bg-indigo-100 text-indigo-800',
      'JavaScript': 'bg-amber-100 text-amber-800',
      'Vue.js': 'bg-emerald-100 text-emerald-800',
      'Angular': 'bg-red-100 text-red-800',
      'Next.js': 'bg-gray-100 text-gray-800',
      'Express': 'bg-purple-100 text-purple-800',
      'MongoDB': 'bg-teal-100 text-teal-800'
    };
    return colors?.[tech] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div 
      className={`group relative bg-card border border-border rounded-xl overflow-hidden shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${isHovered ? 'border-primary/30' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={project?.image}
          alt={project?.imageAlt}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Overlay Actions */}
        <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button variant="default" size="sm" iconName="ExternalLink" iconPosition="left" onClick={() => onViewDemo(project)} className="bg-white/90 text-gray-900 hover:bg-white">Live Demo</Button>
          <Button variant="outline" size="sm" iconName="Github" iconPosition="left" onClick={() => onViewCode(project)} className="bg-white/90 text-gray-900 border-white/90 hover:bg-white">Code</Button>
        </div>

        {/* Status Badge */}
        {project?.status && (
          <div className="absolute top-3 right-3">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${project?.status === 'Live' ? 'bg-green-100 text-green-800' : project?.status === 'In Progress'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
              {project?.status}
            </span>
          </div>
        )}
      </div>
      {/* Project Content */}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">{project?.title}</h3>
            <p className="text-sm text-muted-foreground">{project?.category}</p>
          </div>
          {project?.featured && (
            <Icon name="Star" size={16} className="text-yellow-500 fill-current" />
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{project?.description}</p>

        {/* Tech Stack */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project?.technologies?.slice(0, 3)?.map((tech, index) => (
            <span key={index} className={`px-2 py-1 text-xs font-medium rounded-md ${getTechColor(tech)}`}>{tech}</span>
          ))}
          {project?.technologies?.length > 3 && (
            <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600">+{project?.technologies?.length - 3} more</span>
          )}
        </div>

        {/* Metrics */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1"><Icon name="Star" size={12} /><span>{project?.stars}</span></div>
            <div className="flex items-center gap-1"><Icon name="GitFork" size={12} /><span>{project?.forks}</span></div>
            <div className="flex items-center gap-1"><Icon name="Users" size={12} /><span>{project?.users}</span></div>
          </div>
          <span>{project?.lastUpdated}</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" iconName="Eye" iconPosition="left" onClick={() => onViewDemo(project)} className="flex-1">View Details</Button>
          <Button variant="ghost" size="sm" iconName="Github" onClick={() => onViewCode(project)}><Icon name="Github" size={16} /></Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
